import React, { useState } from 'react';
import { Row } from '@tanstack/react-table';
import {
  MoreVertical,
  Download,
  UserPlus,
  Trash2,
  Info,
  PencilLine,
  Copy,
  ExternalLink,
  Users,
  FolderInput,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import ConfirmationModal from 'components/blocks/confirmation-modal/confirmation-modal';
import { Button } from 'components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'components/ui/tooltip';

import { IFileData } from '../hooks/use-mock-files-query';
import { useCommonStore } from 'state/store/common/useCommonStore';
import { VectorizationStatus } from '../services/document-manager-service';
import { useIsMediaLibrary } from 'hooks/use-is-media-library';
import { useConfig } from 'hooks/use-config';
import { getFileBlobUrl } from 'services/storage-service';
import { openMediaLibraryFile } from '../utils/open-media-library-file';
import PDFViewer from './pdf-viewer';

interface FileTableRowActionsProps {
  row: Row<IFileData>;
  onViewDetails: (file: IFileData) => void;
  onDownload?: (file: IFileData) => void;
  onShare?: (file: IFileData) => void;
  onDelete?: (file: IFileData) => void;
  onCopy?: (file: IFileData) => void;
  onMove?: (file: IFileData) => void;
  onOpen?: (file: IFileData) => void;
  onRename?: (file: IFileData) => void;
  onSharedWithTenant?: (file: IFileData) => void;
}

export function FileTableRowActions({
  row,
  onViewDetails,
  onDownload,
  onShare,
  onDelete,
  onMove,
  onCopy,
  onOpen,
  onRename,
  onSharedWithTenant,
}: Readonly<FileTableRowActionsProps>) {
  const { t } = useTranslation();
  const file = row.original;
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isFolderDeleteOpen, setIsFolderDeleteOpen] = useState(false);
  const [isDeletingFolder, setIsDeletingFolder] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | undefined>(undefined);
  const isAdmin = useCommonStore((state) => state.isAdmin);
  const isOnlyAppUser = useCommonStore((state) => state.isOnlyAppUser);
  const isMediaLibrary = useIsMediaLibrary();
  const { configurationSettings } = useConfig();
  const isPhoto = file.fileType === 'Photo' || file.fileType === 'Image';
  const isPdf = file.fileType === 'Pdf';
  const isGoogleDocsPreview =
    file.fileType === 'Document' ||
    file.fileType === 'Xlsx' ||
    file.fileType === 'Spreadsheet' ||
    file.fileType === 'Pptx' ||
    file.fileType === 'Csv';

  const handleViewFile = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDropdownOpen(false);

    if (isMediaLibrary && (isPdf || isGoogleDocsPreview)) {
      void openMediaLibraryFile(file, configurationSettings);
      return;
    }

    if (isMediaLibrary && isPhoto) {
      setTimeout(async () => {
        const url = await getFileBlobUrl(file.id.toString(), configurationSettings, undefined);
        if (url) setPreviewUrl(url);
      }, 0);
      return;
    }

    if (isMediaLibrary) return;

    setTimeout(() => {
      onViewDetails(file);
    }, 0);
  };

  const handleFolderDeleteClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDropdownOpen(false);
    setIsFolderDeleteOpen(true);
  };

  const handleConfirmFolderDelete = async () => {
    if (!onDelete) return;
    setIsDeletingFolder(true);
    try {
      await onDelete(file);
    } catch {
      // The existing delete handler toasts the failure.
    } finally {
      setIsDeletingFolder(false);
      setIsFolderDeleteOpen(false);
    }
  };

  const handleItemClick = (action: (file: IFileData) => void) => (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    setIsDropdownOpen(false);
    setTimeout(() => {
      action(file);
    }, 0);
  };

  const handleDropdownTriggerClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDropdownOpen((prev) => !prev);
  };

  const handleDropdownTriggerKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      setIsDropdownOpen((prev) => !prev);
    }
  };

  const handleDropdownOpenChange = (open: boolean) => {
    setIsDropdownOpen(open);
  };

  const handleDropdownContentClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const handleDropdownContentKeyDown = (e: React.KeyboardEvent) => {
    e.stopPropagation();
  };

  const canDownload = onDownload !== undefined;
  const canDelete = onDelete !== undefined;
  const canShare = onShare !== undefined;
  const canRename = onRename !== undefined;
  const canCopy = onCopy !== undefined;
  const canOpen = onOpen !== undefined;
  const canMove = onMove !== undefined;
  const canSharedWithTenant = onSharedWithTenant !== undefined && isAdmin;
  const isLibraryFolder = isMediaLibrary && file.fileType === 'Folder';

  const menuLabel = t('MORE_OPTIONS', { defaultValue: 'More options' });

  return (
    <>
    <TooltipProvider delayDuration={300}>
    <Tooltip>
    <DropdownMenu open={isDropdownOpen} onOpenChange={handleDropdownOpenChange}>
      <DropdownMenuTrigger asChild>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            className="h-8 w-8 p-0"
            aria-label={menuLabel}
            onClick={handleDropdownTriggerClick}
            onKeyDown={handleDropdownTriggerKeyDown}
            onPointerDown={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <span className="sr-only">{menuLabel}</span>
            <MoreVertical className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
      </DropdownMenuTrigger>
      <TooltipContent side="top">{menuLabel}</TooltipContent>
      <DropdownMenuContent
        align="end"
        className="w-48"
        onClick={handleDropdownContentClick}
        onKeyDown={handleDropdownContentKeyDown}
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        {isLibraryFolder ? (
          <>
            {canRename && (
              <DropdownMenuItem onClick={handleItemClick(onRename)}>
                <PencilLine className="mr-2 h-4 w-4" />
                {t('RENAME')}
              </DropdownMenuItem>
            )}
            {canDelete && (
              <DropdownMenuItem
                onClick={handleFolderDeleteClick}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                {t('DELETE', { defaultValue: 'Delete' })}
              </DropdownMenuItem>
            )}
          </>
        ) : (
          <>
        {canOpen && (
          <DropdownMenuItem onClick={handleItemClick(onOpen)}>
            <ExternalLink className="mr-2 h-4 w-4" />
            {t('OPEN')}
          </DropdownMenuItem>
        )}

        {canShare && !isOnlyAppUser && (
          <DropdownMenuItem disabled={file.isSharedWithTenant} onClick={handleItemClick(onShare)}>
            <UserPlus className="mr-2 h-4 w-4" />
            {t('SHARE')}
          </DropdownMenuItem>
        )}

        {canSharedWithTenant && !isOnlyAppUser && (
          <DropdownMenuItem onClick={handleItemClick(onSharedWithTenant)}>
            <Users className="mr-2 h-4 w-4" />
            {file.isSharedWithTenant ? t('UNSHARE_FROM_TENANT') : t('SHARE_WITH_TENANT')}
          </DropdownMenuItem>
        )}

        {canCopy && (
          <DropdownMenuItem onClick={handleItemClick(onCopy)}>
            <Copy className="mr-2 h-4 w-4" />
            {t('COPY')}
          </DropdownMenuItem>
        )}
        {canMove && (
          <DropdownMenuItem onClick={handleItemClick(onMove)}>
            <FolderInput className="mr-2 h-4 w-4" />
            {t('MOVE', { defaultValue: 'Move to folder' })}
          </DropdownMenuItem>
        )}

        {canRename && !isMediaLibrary && (
          <DropdownMenuItem onClick={handleItemClick(onRename)}>
            <PencilLine className="mr-2 h-4 w-4" />
            {t('RENAME')}
          </DropdownMenuItem>
        )}

        <DropdownMenuItem onClick={handleViewFile}>
          <Info className="mr-2 h-4 w-4" />
          {isMediaLibrary ? t('VIEW_FILE', { defaultValue: 'View File' }) : t('VIEW_DETAILS')}
        </DropdownMenuItem>

        {canDownload && (
          <DropdownMenuItem onClick={handleItemClick(onDownload)}>
            <Download className="mr-2 h-4 w-4" />
            {t('DOWNLOAD')}
          </DropdownMenuItem>
        )}

        {canDelete && !isMediaLibrary && (
          <DropdownMenuItem
            onClick={handleItemClick(onDelete)}
            className="text-destructive focus:text-destructive"
            disabled={file.vectorizationStatus !== VectorizationStatus.SUCCESS}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            {t('REMOVE')}
          </DropdownMenuItem>
        )}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
    </Tooltip>
    </TooltipProvider>
    {previewUrl ? (
      <PDFViewer url={previewUrl} setUrl={(url) => setPreviewUrl(url || undefined)} fileType="Photo" />
    ) : null}
    {isLibraryFolder && canDelete ? (
      <ConfirmationModal
        open={isFolderDeleteOpen}
        onOpenChange={setIsFolderDeleteOpen}
        title={t('DELETE_FOLDER', { defaultValue: 'Delete folder' })}
        description={t('DELETE_LIBRARY_FOLDER_CONFIRMATION', {
          name: file.name,
          defaultValue:
            'Delete "{{name}}"? This folder and any folders inside it will be removed. Files in them will move back to the library.',
        })}
        onConfirm={handleConfirmFolderDelete}
        confirmText={t('DELETE', { defaultValue: 'Delete' })}
        cancelText={t('CANCEL', { defaultValue: 'Cancel' })}
        confirmVariant="delete"
        preventAutoClose
        isLoading={isDeletingFolder}
      />
    ) : null}
    </>
  );
}
